import { getNextBarTime, parseResolution, socketUrl } from './helpers'
import { millisecondsToSeconds } from 'date-fns'
import {
  BirdeyeTxsDataMessage,
  birdeyeTradeToTrade,
  buildBirdeyeTxsSubscription,
  buildBirdeyeTxsUnsubscription,
} from './trades'
import { Trade } from 'hooks/useRecentTrades'

interface BaseQuotePriceDataMessage {
  type: 'BASE_QUOTE_PRICE_DATA'
  data: {
    o: number
    h: number
    l: number
    c: number
    eventType: 'ohlcv'
    type: string
    unixTime: number
    v: number
    baseAddress: string
    quoteAddress: string
  }
}

interface ChartSubscription {
  resolution: string
  lastBar: Bar | null | undefined
  callback: (data: Bar) => void
  baseAddress: string
  quoteAddress: string
}

interface TradesSubscription {
  baseMint: string
  quoteMint: string | null
  onTrade: (trade: Trade) => void
}

interface Bar {
  low: number
  high: number
  open: number
  close: number
  volume: number
  time: number
}

// --- Singleton state ------------------------------------------------------
// Single WebSocket multiplexed across chart bars and trades feed. The proxy
// only allows one connection per origin, so both subscription types share it.
// Active subscriptions are stored at module scope so we can replay them on
// reconnect — the previous design didn't do this and a single socket close
// (e.g., chart unmount calling closeSocket) silently broke realtime updates.

// KILL SWITCH: Birdeye WS usage spiked (suspected abuse of the public
// cabana-ws-proxy endpoint / leaked key, ~2k unique connections in 30d).
// While true, no socket is ever opened; subscribers stay registered but get
// no realtime data. Trades still update via 5s REST polling
// (useRecentTrades); chart bars won't tick live. Flip back to false once the
// key is rotated and the proxy authenticates callers.
const WEBSOCKETS_DISABLED = true

let socket: WebSocket | null = null
let chartSubscription: ChartSubscription | null = null
let tradesSubscription: TradesSubscription | null = null

let reconnectAttempts = 0
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let pingTimer: ReturnType<typeof setInterval> | null = null

const MAX_RECONNECT_ATTEMPTS = 60
const PING_INTERVAL_MS = 30_000
const MAX_BACKOFF_MS = 30_000
const INITIAL_BACKOFF_MS = 500

function calcBackoff(attempt: number): number {
  return Math.min(INITIAL_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS)
}

function isReady(ws: WebSocket | null): ws is WebSocket {
  return ws !== null && ws.readyState === WebSocket.OPEN
}

function safeSend(payload: string): void {
  if (!isReady(socket)) return
  try {
    socket.send(payload)
  } catch (e) {
    console.warn('[birdeye-ws] send failed', e)
  }
}

function sendChartSubscribe(sub: ChartSubscription) {
  if (!sub.baseAddress || !sub.quoteAddress) return
  safeSend(
    JSON.stringify({
      type: 'SUBSCRIBE_BASE_QUOTE_PRICE',
      data: {
        chartType: parseResolution(sub.resolution),
        baseAddress: sub.baseAddress,
        quoteAddress: sub.quoteAddress,
      },
    }),
  )
}

function sendTradesSubscribe(sub: TradesSubscription) {
  safeSend(buildBirdeyeTxsSubscription(sub.baseMint))
}

function startPingLoop() {
  stopPingLoop()
  pingTimer = setInterval(() => {
    safeSend(JSON.stringify({ type: 'PING' }))
  }, PING_INTERVAL_MS)
}

function stopPingLoop() {
  if (pingTimer != null) {
    clearInterval(pingTimer)
    pingTimer = null
  }
}

function scheduleReconnect() {
  if (reconnectTimer != null) return
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    console.error('[birdeye-ws] max reconnect attempts reached')
    return
  }
  const delay = calcBackoff(reconnectAttempts)
  reconnectAttempts += 1
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    connect()
  }, delay)
}

function handleOpen() {
  console.debug('[birdeye-ws] connected')
  reconnectAttempts = 0
  startPingLoop()
  // Replay live subscriptions so the user doesn't have to navigate away/back.
  if (chartSubscription) sendChartSubscribe(chartSubscription)
  if (tradesSubscription) sendTradesSubscribe(tradesSubscription)
}

function handleClose(event?: CloseEvent) {
  console.debug('[birdeye-ws] closed', event?.code, event?.reason)
  stopPingLoop()
  // Detach listeners from the dying socket so any tail events from it can't
  // race against the next connection attempt.
  if (socket) {
    socket.removeEventListener('open', handleOpen)
    socket.removeEventListener('close', handleClose)
    socket.removeEventListener('error', handleError)
    socket.removeEventListener('message', handleMessage)
  }
  socket = null
  scheduleReconnect()
}

function handleError(event: Event) {
  console.warn('[birdeye-ws] error', event)
  // close handler will fire next; reconnection happens there.
}

function handleMessage(msg: MessageEvent<string>) {
  let data: BaseQuotePriceDataMessage | BirdeyeTxsDataMessage | undefined
  try {
    data = JSON.parse(msg.data) as
      | BaseQuotePriceDataMessage
      | BirdeyeTxsDataMessage
  } catch {
    return
  }
  if (!data?.type) return

  if (data.type === 'BASE_QUOTE_PRICE_DATA') {
    handleBaseQuotePrice(data)
  } else if (data.type === 'TXS_DATA') {
    handleTradeUpdate(data)
  }
}

function connect() {
  if (WEBSOCKETS_DISABLED) return
  if (
    socket &&
    (socket.readyState === WebSocket.OPEN ||
      socket.readyState === WebSocket.CONNECTING)
  ) {
    return
  }
  try {
    socket = new WebSocket(socketUrl, 'echo-protocol')
    socket.addEventListener('open', handleOpen)
    socket.addEventListener('close', handleClose)
    socket.addEventListener('error', handleError)
    socket.addEventListener('message', handleMessage)
  } catch (e) {
    console.warn('[birdeye-ws] connect failed', e)
    scheduleReconnect()
  }
}

// Connect once at module load. Browser-only — guard for SSR/tests.
if (typeof window !== 'undefined') {
  connect()
}

// --- Message handlers -----------------------------------------------------

function handleBaseQuotePrice(data: BaseQuotePriceDataMessage) {
  if (!chartSubscription) return
  const currTime = data.data.unixTime * 1000
  const lastBar = chartSubscription.lastBar

  if (
    data.data.baseAddress !== chartSubscription.baseAddress ||
    data.data.quoteAddress !== chartSubscription.quoteAddress
  )
    return

  const resolution = chartSubscription.resolution
  const nextBarTime = lastBar ? getNextBarTime(lastBar.time, resolution) : null

  let bar: Bar
  if (nextBarTime != null && currTime >= nextBarTime) {
    bar = {
      time: nextBarTime,
      open: data.data.o,
      high: data.data.h,
      low: data.data.l,
      close: data.data.c,
      volume: data.data.v,
    }
  } else {
    bar = {
      ...(lastBar ?? { open: 0, time: millisecondsToSeconds(Date.now()) }),
      high: Math.max(lastBar?.high ?? 0, data.data.h),
      low: Math.min(lastBar?.low ?? 0, data.data.l),
      close: data.data.c,
      volume: data.data.v,
    }
  }

  chartSubscription.lastBar = bar
  chartSubscription.callback(bar)
}

function handleTradeUpdate(data: BirdeyeTxsDataMessage) {
  if (!tradesSubscription || !data.data) return
  const trade = birdeyeTradeToTrade(data.data, tradesSubscription.baseMint)
  if (!trade) return
  // No quote-mint filter: REST returns swaps for the base across all pairs,
  // and WS does the same so the live + historical streams match. Trade.price
  // is USD-denominated, so mixed-quote rows still display sensibly.
  tradesSubscription.onTrade(trade)
}

// --- Public API -----------------------------------------------------------

export function subscribeToTrades(args: {
  baseMint: string
  quoteMint?: string
  onTrade: (trade: Trade) => void
}): () => void {
  const sub: TradesSubscription = {
    baseMint: args.baseMint,
    quoteMint: args.quoteMint || null,
    onTrade: args.onTrade,
  }
  tradesSubscription = sub
  // If the socket is open, send immediately. Otherwise the open handler will
  // replay it once we (re)connect.
  if (isReady(socket)) {
    sendTradesSubscribe(sub)
  } else {
    connect()
  }

  return () => {
    if (tradesSubscription !== sub) return
    tradesSubscription = null
    safeSend(buildBirdeyeTxsUnsubscription())
  }
}

export function subscribeOnStream(
  symbolInfo: { base_token: string; quote_token: string },
  resolution: string,
  onRealtimeCallback: (data: Bar) => void,
  lastBar: Bar | null | undefined,
) {
  const sub: ChartSubscription = {
    resolution,
    lastBar,
    callback: onRealtimeCallback,
    baseAddress: symbolInfo.base_token,
    quoteAddress: symbolInfo.quote_token,
  }
  chartSubscription = sub

  if (isReady(socket)) {
    sendChartSubscribe(sub)
  } else {
    connect()
  }
}

export function unsubscribeFromStream() {
  chartSubscription = null
  safeSend(JSON.stringify({ type: 'UNSUBSCRIBE_BASE_QUOTE_PRICE' }))
}

// Intentionally a no-op now: the singleton is shared infra (chart bars + the
// trades feed). Closing it on chart unmount used to silently break realtime
// updates because there was no reconnect logic. Use unsubscribeFromStream()
// to drop a subscription; the socket itself stays open and the browser will
// tear it down automatically when the page unloads.
export function closeSocket() {}

export function isOpen(ws?: WebSocket) {
  const sock = ws ?? socket
  return sock?.readyState === WebSocket.OPEN
}
